/*
 * Nosto Search JS
 * Handles Nosto-powered search and category pages, including facets,
 * pagination, and product rendering.
 */

class NostoSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.searchTerm = '';
    this.searchableFacets = ['brand'];
    this.openedFacets = [];
    this.activeMaxPrice = null;
    this.lastEditedFacet = null;
    this.includeFacetRules = { 'beauty': 'sub-category' };
    this.excludeFacets = [];
    this.usingFallback = false;
    this.fallbackAttemptTime = null;
    this.retryCount = 0;
    this.maxRetries = 3;
    this.initialLoadComplete = false;
  }

  connectedCallback() {
    try {
      this.options = JSON.parse(this.getAttribute('data-options') || '{}');
    } catch (error) {
      console.error('Invalid nosto-search options:', error);
      this.options = {};
    }
    this.maxProducts = this.options.maxProducts ? parseInt(this.options.maxProducts) : null;
    this.loadingSpinner = this.querySelector('.loading-overlay');
    this.productGrid = this.querySelector('[data-product-grid-nosto]');
    this.container = this;

    this.gridySlider = this.querySelector('gridy-slider');

    this.iprDesktop = this.options.iprDesktop ?
      parseInt(this.options.iprDesktop) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprDesktop) : null);

    this.iprTablet = this.options.iprTablet ?
      parseInt(this.options.iprTablet) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprTablet) : null);

    this.iprMobile = this.options.iprMobile ?
      parseInt(this.options.iprMobile) :
      (this.gridySlider ? parseInt(this.gridySlider.dataset.iprMobile) : null);

    this.arrows = this.querySelector('.gridy-arrows');

    this.searchForm = this.querySelector('form[role="search"]');
    this.input = this.querySelector('input[type="search"]');
    this.resultsCount = this.querySelector('#products-count-ajax');
    this.header = this.querySelector('header [data-search-title]');
    this.facetsFormContainer = document.querySelector('.filter-form');
    this.abortController = new AbortController();
    this.sortBy = this.querySelector('#sort-by');
    this.viewMoreButton = this.querySelector('#pagination-ajax [data-load-more]');
    this.showPreviousLink = this.querySelector('[data-show-previous]');
    this.showCount = this.querySelector('[data-show-count]');
    this.resultsSize = parseInt(this.productGrid?.getAttribute('data-results-per-page')) || 24;
    this.context = this.productGrid?.getAttribute('data-context');
    this.collectionId = this.productGrid?.getAttribute('data-collection-id') || null;
    this.collectionTitle = this.productGrid?.getAttribute('data-collection') || null;

    if (this.context === 'serp') {
      this.excludeFacets.push('sub-category');
    }

    this.enableFallback = JSON.parse(this.options.enableFallback || 'false');

    // ?page=N past the last page: the server renders a message instead of the grid
    if (this.querySelector('[data-out-of-range]')) return;

    if (!this.productGrid) {
      console.error('Nosto: Required product grid element not found');
      return;
    }

    // Cards are built and hydrated by the shared renderer (component-nosto-card.js),
    // which the editorial carousel uses too.
    this.cardRenderer = Woolman.NostoCard.create({
      cardType: this.options.cardType,
      cardTextAlign: this.options.cardTextAlign,
      cardImgAspectRatio: this.options.cardImgAspectRatio,
      cardImgSizingRule: this.options.cardImgSizingRule,
      isFashion: this._isFashionCollection(),
      imageSizes: this._cardImageSizes(),
      // A slider shows one row, so nothing is reliably above the fold; the grid
      // eager-loads its first visible row.
      eagerCount: () => {
        if (this.gridySlider) return 0;
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
        return isMobile ? (this.iprMobile || 2) : (this.iprDesktop || 4);
      }
    });

    this.setLoadingState();
    this.setupClickTracking();
    this.setupEventListeners();
    this.initializeFromURL();
  }

  _generateContentSlotElement(contentSlot) {
    const contentSlotUrl = contentSlot.link;
    const contentSlotImageAlt = contentSlot.imageAltText || '';
    const contentSlotImageUrl = contentSlot.image || '';
    const contentSlotPosition = contentSlot.position;
    const contentSlotKey = contentSlot.key || `${contentSlotPosition || ''}|${contentSlotUrl || ''}|${contentSlotImageUrl || ''}`;

    const contentCardTemplate = document.querySelector('.js-card-content-template');
    const contentCardTemplateContent = contentCardTemplate.content.cloneNode(true);
    const contentCardContainer = document.createElement('div');
    contentCardContainer.appendChild(contentCardTemplateContent);
    Woolman.NostoCard.setCardUrl(contentCardContainer, contentSlotUrl);
    // Without alt text the label would only say "View"
    if (contentSlotImageAlt) {
      const contentAriaLabel = window.accessibilityStrings.viewSomething.replace('{{ something }}', () => contentSlotImageAlt);
      contentCardContainer.querySelector('.card-media').setAttribute('aria-label', contentAriaLabel);
    }
    this.cardRenderer.setProductImage(contentCardContainer, contentSlotImageUrl, contentSlotImageAlt, contentSlotPosition, null);
    const slotElement = contentCardContainer.querySelector('.card--content-slot');
    if (slotElement) {
      slotElement.setAttribute('data-content-slot-key', contentSlotKey);
    }
    return slotElement;
  }

  /**
   * `sizes` for card images, so the browser picks a candidate close to the
   * rendered width instead of assuming the full viewport.
   *
   * From 1000px the grid sits beside a 264px filter sidebar with a 48px gap,
   * which leaves it roughly 70% of the container; below that it spans the
   * container. Slight over-estimates are harmless, under-estimates are not.
   */
  _cardImageSizes() {
    const mobile = this.iprMobile || 2;
    const tablet = this.iprTablet || 3;
    const desktop = this.iprDesktop || 4;
    const pct = (columns, share) => Math.ceil((100 / columns) * share);

    return [
      `(max-width: 767px) ${pct(mobile, 1)}vw`,
      `(max-width: 999px) ${pct(tablet, 1)}vw`,
      `${pct(desktop, 0.7)}vw`
    ].join(', ');
  }

  /**
   * Check whether the current collection is in the configured fashion handles list.
   * Fashion collections show all available product images in the carousel;
   * all other collections are capped at 2 images.
   */
  _isFashionCollection() {
    const handles = (this.options.fashionCollectionHandles || '')
      .split(',')
      .map(h => h.trim().toLowerCase())
      .filter(Boolean);
    const currentHandle = (this.options.currentCollectionHandle || '').trim().toLowerCase();
    return handles.length > 0 && Boolean(currentHandle) && handles.includes(currentHandle);
  }

  /**
   * Helper method to handle slider arrows visibility
   */
  _handleArrowsVisibility(products) {
    try {
      if (this.gridySlider && this.iprDesktop && this.arrows) {
        if (products.length <= this.iprDesktop) {
          this.arrows.style.display = 'none';
        } else {
          // Ensure arrows are shown if there are more products than iprDesktop
          this.arrows.style.display = '';
        }
      }
    } catch (error) {
      console.error('Error handling arrows visibility:', error);
    }
  }
  /**
   * Set loading state for this placement
   */
  setLoadingState() {
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.remove('hidden');
    }
    if (this.container) {
      this.container.setAttribute('loading', '');
    }
    if (this.productGrid) {
      this.productGrid.setAttribute('loading', '');
    }
  }

  /**
   * Remove loading state for this placement
   */
  removeLoadingState() {
    if (this.loadingSpinner) {
      this.loadingSpinner.classList.add('hidden');
    }
    if (this.container) {
      this.container.removeAttribute('loading');
    }
    if (this.productGrid) {
      this.productGrid.removeAttribute('loading');
    }
  }

  /**
   * Hide the wrapper element for this placement
   */
  hideWrapper() {
    if (this.container) {
      this.container.classList.add('hidden');
    }
    if (this.productGrid) {
      this.productGrid.classList.add('hidden');
    }
  }

  /**
   * Set up click tracking for product cards using event delegation
   */
  setupClickTracking() {
    if (this.productGrid) {
      document.addEventListener('quickBuy:modalOpened', (event) => {
        const productId = event.detail.productId;
        const productUrl = event.detail.productUrl;
        if (productUrl && productId) {
          this.trackSearchClick(productId, productUrl);
        }
      });
      this.productGrid.addEventListener('click', this.handleProductClick.bind(this));
    }
  }

  /**
   * Handle click events on product cards and track via Nosto API
   * @param {Event} event - Click event
   */
  handleProductClick(event) {
    if (event.target.closest('quick-buy')) return;
    const card = event.target.classList.contains('card--product') ? event.target : event.target.closest('.card--product');
    if (!card) return;

    const productId = card.getAttribute('data-product-id') || card.getAttribute('data-track-product-id');
    const productUrl = `${window.location.origin}${card.querySelector('a')?.getAttribute('href')}`;

    if (productUrl && productId) {
      this.trackSearchClick(productId, productUrl);
    }
  }

  /**
   * Track when a product in search results is clicked
   * @param {string} productId - ID of the product that was clicked
   * @param {string} productUrl - URL of the product page
   */
  trackSearchClick(productId, productUrl) {
    if (!window.nostojs) return;
    nostojs(api => {
      api.recordSearchClick(
        this.context,
        {
          productId: productId,
          url: productUrl
        }
      );
    });
  }

  /**
   * URL of a results page: current params (filters, sort, q), no `view`, and no
   * `page` param for page 1. Matches the server-rendered pagination links.
   */
  pageUrl(page) {
    const params = new URLSearchParams(window.location.search);
    params.delete('view');
    if (page > 1) {
      params.set('page', page);
    } else {
      params.delete('page');
    }
    const paramsStr = params.toString();
    return paramsStr ? `${window.location.pathname}?${paramsStr}` : window.location.pathname;
  }

  /**
   * Set up initial state from URL parameters
   */
  initializeFromURL() {
    const params = new URLSearchParams(window.location.search);
    // Pages loaded into the grid: page N on arrival, extended by load more
    this.firstLoadedPage = parseInt(params.get('page')) || 1;
    this.lastLoadedPage = this.firstLoadedPage;

    // Handle keyword parameter
    const isKeyword = params.get('keyword') === 'true';
    if (isKeyword) {
      params.set('keyword', 'true');
    }

    // Update URL and sort options
    this.updateQuery(params);
    if (params.has('sort_by') && this.sortBy) {
      this.sortBy.value = params.get('sort_by');
    }

    // Set loading state and fetch results
    this.setLoadingState();
    this.getSearchResults();
  }

  /**
   * Set up all event listeners for the search component
   */
  setupEventListeners() {
    // Store bound event handlers as class properties
    this.boundFacetChangeHandler = this.onFacetChange.bind(this);
    this.boundFacetClearHandler = this.onFacetClear.bind(this);

    // Use optional chaining to prevent errors if elements don't exist
    this.searchForm?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.sortBy?.addEventListener('change', this.onSelectChange.bind(this));
    this.viewMoreButton?.addEventListener('click', this.onViewMoreClick.bind(this));
    this.showPreviousLink?.addEventListener('click', this.onShowPreviousClick.bind(this));
  }

  /**
   * Clean up event listeners when element is removed
   */
  disconnectedCallback() {
    // Clean up abort controller
    if (this.abortController) {
      this.abortController.abort();
    }

    // Remove event listeners from facet elements
    this.facetsFormContainer?.querySelectorAll('input').forEach((item) => {
      item.removeEventListener('change', this.boundFacetChangeHandler);
    });

    this.facetsFormContainer?.querySelectorAll('.facet-clear--single').forEach((item) => {
      item.removeEventListener('click', this.boundFacetClearHandler);
    });

    // Clean up event listeners from main elements
    this.searchForm?.removeEventListener('submit', this.onFormSubmit);
    this.sortBy?.removeEventListener('change', this.onSelectChange);
    this.viewMoreButton?.removeEventListener('click', this.onViewMoreClick);
    this.showPreviousLink?.removeEventListener('click', this.onShowPreviousClick);
  }

  onFormSubmit(event) {
    event.preventDefault();
    this.clearFilters();
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  onSelectChange(event) {
    if (this.usingFallback) {
      // Let Shopify sorting handle it
      return;
    }
    const value = event.target.value;
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', value);
    // A new sort starts from page 1 and replaces the grid
    params.delete('page');
    this.firstLoadedPage = 1;
    this.lastLoadedPage = 1;
    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  /**
   * Plain left clicks on the pagination links load in place; modified clicks
   * (new tab/window) and the Shopify fallback follow the link's href.
   */
  isPlainClick(event) {
    return !this.usingFallback && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  /**
   * A request is in flight (the first load included): a second one would
   * consume its append/prepend flag and replace the grid
   */
  isLoading() {
    return this.productGrid?.hasAttribute('loading');
  }

  onShowPreviousClick(event) {
    if (!this.isPlainClick(event)) return;
    event.preventDefault();
    if (this.isLoading() || this.firstLoadedPage <= 1) return;

    // Prepend the page before the first loaded one; the URL stays on the page the visitor arrived on
    const page = this.firstLoadedPage - 1;
    this.prependResults = true;
    this.setLoadingState();
    this.getNostoSearchResults(page);
    this.getSearchResults(page);
  }

  onViewMoreClick(event) {
    if (!this.isPlainClick(event)) return;
    event.preventDefault();
    if (this.isLoading()) return;
    const params = new URLSearchParams(window.location.search);

    // Append the page after the last loaded one
    this.lastLoadedPage++;
    params.set('page', this.lastLoadedPage);
    this.appendResults = true;

    // Remove legacy 'from' parameter if it exists
    params.delete('from');

    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  updateQuery(params = null) {
    params = params ? params : new URLSearchParams(window.location.search);
    if (this.context == 'serp') {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;
      this.searchTerm = searchTerm;
      params.set('q', searchTerm);
    }
    const queryStr = params.toString();
    const url = !queryStr ? window.location.pathname : `${window.location.pathname}?${queryStr}`;
    window.history.replaceState({ path: url }, '', url);
  }

  clearFilters() {
    const params = new URLSearchParams(window.location.search);
    this.activeMaxPrice = null;
    for (const [param, value] of params.entries()) {
      if (param.startsWith('filter.')) {
        params.delete(param);
      }
    }
    // Reset to page 1 when clearing filters
    params.delete('page');
    this.firstLoadedPage = 1;
    this.lastLoadedPage = 1;
    this.updateQuery(params);
  }

  getQuery() {
    return this.input.value.trim();
  }

  /**
   * Get search results from cache, Nosto API or fallback to Shopify
   */
  async getSearchResults(page = null) {
    // Update header text for search context
    if (this.context === 'serp' && this.header) {
      this.header.textContent = `${window.nostoStrings?.searchResultsFor || 'Search results for'} "${this.searchTerm}"`;
    }
    // The URL doesn't change on "Show previous", so the page is part of the key
    const cacheParams = new URLSearchParams(location.search);
    const requestedPage = page || parseInt(cacheParams.get('page')) || 1;
    cacheParams.delete('page');
    const cacheKey = `${location.pathname}?${cacheParams.toString()}|${requestedPage}`;

    // Shopify's own filters are in place: Nosto's controls are gone, so stay on the fallback until the next page load
    if (this.hasAttribute('data-native-filters')) {
      return this.showServerResults();
    }

    // Reloaded by the fallback after a Nosto failure: show the server cards without waiting for Nosto
    if (this.enableFallback && sessionStorage.getItem('nostoFallbackUrl') === location.pathname + location.search) {
      sessionStorage.removeItem('nostoFallbackUrl');
      return this.showServerResults({ reloaded: true });
    }

    // Try to get cached results first for better performance
    if (!this.usingFallback && this.cachedResults[cacheKey]) {
      this.renderSearchResults(this.cachedResults[cacheKey]);
      return;
    }

    // Check if we should skip Nosto due to previous failures
    this.checkNostoAvailability();

    if (this.usingFallback && this.enableFallback) {
      return this.showServerResults();
    }

    try {
      // Create a new abort controller for this request
      this.abortController = new AbortController();

      // Get Nosto search results
      // const nostoResults = await this.results;
      // A missing preload (broken script) or an invalid response is a failure, not "no results"
      if (!window.preloadedNostoResults) {
        throw new Error('Nosto results were not requested');
      }
      const nostoResults = await window.preloadedNostoResults;
      if (!nostoResults?.products) {
        throw new Error('Invalid Nosto API response');
      }

      if (nostoResults.products.hits) {
        // Cache the results for faster subsequent requests
        this.cachedResults[cacheKey] = nostoResults;
        this.renderSearchResults(nostoResults);

        // Reset retry counter on success
        sessionStorage.removeItem('fallbackAttempts');
      } else {
        // Handle empty results
        this.renderNoResults();

        // Reset retry counter as this is a valid response (just empty)
        sessionStorage.removeItem('fallbackAttempts');
      }
    } catch (error) {
      console.error('Nosto search error:', error);
      this.appendResults = false;
      this.prependResults = false;

      // Get current attempt count from sessionStorage
      const currentAttempts = parseInt(sessionStorage.getItem('fallbackAttempts') || '0');
      const newAttempts = currentAttempts + 1;

      // Store updated attempt count
      sessionStorage.setItem('fallbackAttempts', String(newAttempts));

      console.log(`Nosto API attempt ${newAttempts} of ${this.maxRetries} failed`);

      if (newAttempts >= this.maxRetries) {
        console.log('Max attempts reached, setting skipNostoUntil and falling back to Shopify');

        if (this.enableFallback) {
          // Set skipNostoUntil in sessionStorage for 10 minutes
          sessionStorage.setItem('skipNostoUntil', String(Date.now() + 600000));
          return this.showServerResults();
        } else {
          // Show error state if fallback is disabled
          this.removeLoadingState();
          this.renderErrorState();
        }
      } else {
        // Immediately fallback to Shopify without retrying Nosto
        if (this.enableFallback) {
          return this.showServerResults();
        } else {
          // Show error state if fallback is disabled
          this.removeLoadingState();
          this.renderErrorState();
        }
      }
      return;
    }
  }

  /**
   * Check if Nosto should be skipped based on previous failures
   */
  checkNostoAvailability() {
    const skipNostoUntil = sessionStorage.getItem('skipNostoUntil');
    const currentTime = Date.now();

    if (skipNostoUntil && currentTime <= parseInt(skipNostoUntil)) {
      this.usingFallback = true;
      // Reset retry counter when using fallback
      this.retryCount = 0;
    } else {
      this.usingFallback = false;
      // Clear expired setting
      if (skipNostoUntil) {
        sessionStorage.removeItem('skipNostoUntil');
      }
    }
  }

  /**
   * Shopify fallback when Nosto fails, in place (the address never changes to
   * view=fallback): Shopify's own filters, sorting, grid and pagination from the
   * fallback template. If those can't be loaded, the server-rendered cards.
   *
   * @param {Object} options
   * @param {boolean} options.reloaded This load is the fallback's own reload, so never reload again
   */
  async showServerResults({ reloaded = false } = {}) {
    this.usingFallback = true;
    const query = (new URLSearchParams(window.location.search).get('q') || '').trim();

    // Search page without a query: the server renders no cards, so there's nothing to load
    if (this.context === 'serp' && !query) {
      this.setAttribute('data-fallback', '');
      this.productGrid.querySelector('.skeleton-grid')?.remove();
      this.removeLoadingState();
      return;
    }

    // The server cards show while Shopify's filters load
    if (this.productGrid.querySelector('[data-server-results]')) {
      this.setAttribute('data-fallback', '');
      this.productGrid.querySelector('.skeleton-grid')?.remove();
    }

    try {
      await this.loadNativeResults();
    } catch (error) {
      console.error('Shopify fallback filters failed:', error);
      this.showServerCards({ reloaded });
    }
  }

  /**
   * Loads the fallback template (Shopify's own filters and sorting, Search &
   * Discovery) for the current address and swaps in its filter bar, active
   * filters, filter drawer, grid and pagination; component-facets.js runs them
   * from there. Nosto's filters have no Shopify equivalent, so they're dropped.
   */
  async loadNativeResults() {
    const params = new URLSearchParams(window.location.search);
    const nostoFilters = [...params.keys()].filter((key) => key.startsWith('filter.') && !/^filter\.[pv]\./.test(key));
    nostoFilters.forEach((key) => params.delete(key));
    if (nostoFilters.length) {
      // Page N of a filtered list isn't page N of the full list
      params.delete('page');
    }
    // Nosto's own sort options: the closest Shopify sort on collections, Shopify's default on search
    const nostoSorts = { 'published-ascending': 'created-descending', 'revenue-descending': 'best-selling' };
    const sortBy = params.get('sort_by');
    if (nostoSorts[sortBy]) {
      if (this.context === 'serp') {
        params.delete('sort_by');
      } else {
        params.set('sort_by', nostoSorts[sortBy]);
      }
    }
    params.delete('view');
    const queryStr = params.toString();

    const response = await fetch(`${window.location.pathname}?${queryStr ? `${queryStr}&` : ''}view=fallback`, {
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error(`Fallback template returned ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const nativeGrid = doc.querySelector('#product-grid-ajax');
    if (!nativeGrid) throw new Error('No product grid in the fallback template');

    // Nosto's "no results" state would keep hiding the filter bar and pagination
    this.classList.remove('no-results');

    // Filter bar (count, filter button, sort) and the active filters under it. The parsed nodes
    // are inserted as they are, so their custom elements (sort-by, price-range) start in the page.
    // No filter bar in the fallback (a search without results): Nosto's would be dead, so it goes.
    const nativeActions = doc.querySelector('.product-grid-actions');
    const ownActions = this.querySelector('.product-grid-actions');
    this.querySelector('#current-facets-ajax')?.remove();
    if (nativeActions) {
      if (ownActions) {
        ownActions.replaceWith(nativeActions);
      } else {
        this.productGrid.before(nativeActions);
      }
      const currentFacets = doc.querySelector('#current-facets-ajax');
      if (currentFacets) nativeActions.after(currentFacets);
      nativeActions.querySelector('a[href="#drawer-filter"]')?.addEventListener('click', (event) => {
        event.preventDefault();
        Woolman.ModalsAndDrawers.showModalOrDrawer('drawer-filter', null, null, null, event.pointerType);
      });
    } else {
      ownActions?.remove();
    }

    // Shopify's filter drawer in place of Nosto's; an open drawer is closed first so the page
    // isn't left scroll-locked. Its close buttons and overlay were bound on page load, so bind them here.
    const nativeDrawer = doc.querySelector('#drawer-filter');
    const ownDrawer = document.getElementById('drawer-filter');
    if (ownDrawer?.classList.contains('is-open')) {
      Woolman.ModalsAndDrawers.closeModalOrDrawer('drawer-filter');
    }
    if (nativeDrawer) {
      if (ownDrawer) {
        ownDrawer.replaceWith(nativeDrawer);
      } else {
        document.body.appendChild(nativeDrawer);
      }
      nativeDrawer.querySelectorAll('[data-close], [data-overlay]').forEach((closer) => {
        closer.addEventListener('click', Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent.bind(Woolman.ModalsAndDrawers));
      });
    } else {
      ownDrawer?.remove();
    }

    this.productGrid.innerHTML = nativeGrid.innerHTML;
    const ownPagination = this.querySelector('#pagination-ajax');
    if (ownPagination) ownPagination.innerHTML = doc.querySelector('#pagination-ajax')?.innerHTML || '';
    this.showPreviousLink?.closest('.pagination')?.remove();
    this.showPreviousLink = null;

    window.history.replaceState({}, '', queryStr ? `${window.location.pathname}?${queryStr}` : window.location.pathname);
    // component-facets.js builds its requests from Shopify.queryParams, which it reads only when it
    // loads: rebuild it from this address (e.g. a new search term submitted in place)
    if (window.Shopify && Woolman.Utils?.prepareQueryParams) {
      Shopify.queryParams = {};
      Woolman.Utils.prepareQueryParams();
    }
    this.setAttribute('data-fallback', '');
    this.setAttribute('data-native-filters', '');
    this.removeLoadingState();

    // component-facets.js runs the filters and sorting from here (it reads the address when it loads)
    const facetsScript = doc.querySelector('script[src*="component-facets"]');
    if (facetsScript && !customElements.get('facets-form')) {
      const script = document.createElement('script');
      script.src = facetsScript.getAttribute('src');
      document.body.appendChild(script);
    }
  }

  /**
   * The server-rendered cards of this page, when Shopify's filters can't be
   * loaded either. They're unfiltered and in Shopify's order, so filter and
   * sort params are dropped first (with one reload).
   *
   * @param {Object} options
   * @param {boolean} options.reloaded This load is the fallback's own reload, so never reload again
   */
  showServerCards({ reloaded = false } = {}) {
    const params = new URLSearchParams(window.location.search);
    const filtered = [...params.keys()].some((key) => key.startsWith('filter.') || key === 'sort_by');
    const serverResults = this.productGrid.querySelector('[data-server-results]');
    const query = (params.get('q') || '').trim();

    // A search submitted in place: the server cards are for the term the page loaded with
    const queryChanged = this.context === 'serp' && query !== (this.productGrid.getAttribute('data-server-query') || '').trim();

    // Filtered, a new search term, or Nosto's cards already replaced the server ones: load the server-rendered page
    if (!reloaded && (filtered || queryChanged || !serverResults)) {
      if (filtered) {
        [...params.keys()].filter((key) => key.startsWith('filter.') || key === 'sort_by').forEach((key) => params.delete(key));
        // Page N of a filtered list isn't page N of the full list
        params.delete('page');
      }
      params.delete('view');
      const queryStr = params.toString();
      const url = queryStr ? `${window.location.pathname}?${queryStr}` : window.location.pathname;
      sessionStorage.setItem('nostoFallbackUrl', url);
      window.location.replace(url);
      return;
    }

    this.setAttribute('data-fallback', '');
    this.productGrid.querySelector('.skeleton-grid')?.remove();
    this.removeLoadingState();

    // Counts from the server render
    const total = this.productGrid.getAttribute('data-server-total');
    if (total && this.resultsCount) {
      this.resultsCount.textContent = `${total} ${window.nostoStrings.searchResults}`;
    }
    if (total && serverResults && this.showCount) {
      const countText = window.nostoStrings.searchShowCount || 'Showing {{ count }} of {{ total }} items';
      this.showCount.textContent = countText
        .replace('{{ count }}', serverResults.querySelectorAll('.card--product').length)
        .replace('{{ total }}', total);
    }
  }

  async getNostoSearchResults(page = null) {
    const params = new URLSearchParams(window.location.search);
    const isKeyword = params.get('keyword') == 'true' ? true : false;
    const filters = [];
    const sortBy = [];

    if (params.has('sort_by')) {
      const sortValue = params.get('sort_by');
      // Only Nosto's own sort options (the sort select); relevance is the default. Shopify's own
      // values (best-selling etc.) can be left over from the fallback.
      if (['price-ascending', 'price-descending', 'published-ascending', 'revenue-descending'].includes(sortValue)) {
        let field = sortValue;
        let order = 'asc';
        if (sortValue.endsWith('-ascending')) {
          field = sortValue.replace('-ascending', '');
          order = 'asc';
        } else if (sortValue.endsWith('-descending')) {
          field = sortValue.replace('-descending', '');
          order = 'desc';
        }
        if (!field.includes('price')) {
          field = `stats.${field}`;
        }
        sortBy.push({
          field: field,
          order: order
        });
      }
    }

    // Calculate 'from' based on the requested page ("Show previous") or the URL's page
    const currentPage = page || parseInt(params.get('page')) || 1;
    const from = (currentPage - 1) * this.resultsSize;

    // Iterate through all params to build filters array
    for (const [param, value] of params.entries()) {
      // Skip non-filter params
      // Shopify's own filters (filter.p.*, filter.v.*) are left over from the fallback; Nosto doesn't know them
      if (/^filter\.[pv]\./.test(param)) continue;
      if (!param.startsWith('filter.') || filters.some(f => f.field === 'price')) continue;
      if (param === 'filter.price') {
        const [range, max] = value.split('~');
        const [gte, lte] = range.split('-');
        filters.push({
          field: 'price',
          range: { lte: lte, gte: gte }
        });
      } else {
        // Remove filter. prefix to get param name
        const paramName = param.replace('filter.', '').toLowerCase();
        // Get the corresponding API field from window.filterToNosto
        const apiField = window.filterToNosto && window.filterToNosto[paramName] ? window.filterToNosto[paramName] : paramName;
        if (!apiField) {
          console.error(`Invalid filter parameter: ${paramName}`);
          continue;
        };
        // Split value string into array by pipe character
        const valueArray = value.split('|');
        filters.push({
          field: apiField,
          value: valueArray
        });
      }
    }
    // Parameters for the Nosto. Remember to keep these in sync with nosto-async.liquid (initial load of the page)
    try {
      const searchParams = {
        products: {
          facets: ['*'],
          fields: [
            'name',
            'imageUrl',
            'alternateImageUrls',
            'price',
            'url',
            'brand',
            'listPrice',
            'customFields.key',
            'customFields.value',
            'categoryIds',
            'skus.id',
            'skus.name',
            'skus.price',
            'skus.listPrice',
            'skus.availability'
          ],
          size: this.resultsSize,
          from: from // Use calculated 'from' value
        }
      };

      const trackParams = {
        redirect: true,
        track: this.context,
        isKeyword: isKeyword
      }

      if (this.context === 'serp') {
        searchParams.query = this.searchTerm;
      } else if (this.context === 'category') {
        if (location.pathname !== '/collections/all') {
          searchParams.products.categoryId = this.collectionId;
        }
      }

      if (filters.length > 0) {
        searchParams.products.filter = filters;
      }

      if (sortBy.length > 0) {
        searchParams.products.sort = sortBy;
      }

      // Every failure rejects, including a Nosto client that never answers, so the fallback runs.
      // Keep in sync with nosto-async.liquid.
      window.preloadedNostoResults = Promise.race([
        new Promise((resolve, reject) => {
          nostojs(async api => {
            try {
              if (this.context === 'serp') {
                api.recordSearchSubmit(searchParams.query)
              }
              resolve(await api.search(searchParams, trackParams));
            } catch (error) {
              reject(error);
            }
          });
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Nosto timeout')), 6000))
      ]);

    } catch (error) {
      console.log(error)
      throw error;
    }
  }

  async renderSearchResults(results) {
    this.classList.remove('no-results')
    const products = results.products.hits;
    const resultsFrom = results.products.from;
    const resultsTotal = results.products.total;
    const prepend = this.prependResults;
    this.prependResults = false;

    if (this.viewMoreButton && !prepend) {
      // No link past the last Nosto page in the rendered DOM
      if (resultsTotal <= resultsFrom + products.length) {
        this.viewMoreButton.hidden = true;
        this.viewMoreButton.removeAttribute('href');
      } else {
        this.viewMoreButton.hidden = false;
        this.viewMoreButton.href = this.pageUrl(this.lastLoadedPage + 1);
      }
    }

    const append = this.appendResults;
    this.appendResults = false;

    if (!products || products.length === 0) {
      // Nosto has fewer pages than Shopify (or the page doesn't exist)
      if (resultsFrom > 0 && !append && !prepend) {
        this.renderOutOfRange();
      } else {
        this.renderNoResults();
      }
      return;
    }
    this.processExcludeFacets(products[0]);
    if (this.context === 'serp') {
      document.title = `Search: ${resultsTotal} results found for "${this.searchTerm}"`;
    }
    this.resultsCount.textContent = `${resultsTotal} ${window.nostoStrings.searchResults}`;
    this.productGrid.setAttribute('data-filtered-count', results.products.total);
    this.renderFacets(results.products.facets);
    // Card indexes count from the first product of the whole result set, so content slot positions stay global
    const productsMarkup = await this.cardRenderer.renderMarkup(products, { indexOffset: resultsFrom });
    this._handleArrowsVisibility(products);

    if (append) {
      // For pagination/load more - append without destroying existing nodes/listeners
      this.productGrid.insertAdjacentHTML('beforeend', productsMarkup);
    } else if (prepend) {
      // "Show previous": insert above without moving the cards the visitor is looking at
      const anchor = this.productGrid.firstElementChild;
      const anchorTop = anchor?.getBoundingClientRect().top;
      this.productGrid.insertAdjacentHTML('afterbegin', productsMarkup);
      if (anchor) {
        // Instant: the theme's scroll-behavior: smooth would animate past the new cards
        window.scrollBy({ top: anchor.getBoundingClientRect().top - anchorTop, behavior: 'instant' });
      }
      this.firstLoadedPage--;
    } else {
      // For initial load or filter change - replace content
      this.productGrid.innerHTML = productsMarkup;
    }

    // Check if content slots are available in options
    const contentSlots = this.options.contentSlots;
    if (contentSlots && Array.isArray(contentSlots) && contentSlots.length > 0) {
      // Sort content slots by position to insert them in order
      const sortedSlots = [...contentSlots].sort((a, b) => (a.position || 0) - (b.position || 0));

      // Insert each content slot at its specified position
      sortedSlots.forEach(contentSlot => {
        const contentSlotKey = contentSlot.key || `${contentSlot.position || ''}|${contentSlot.link || ''}|${contentSlot.image || ''}`;
        if (this.productGrid.querySelector(`.card--content-slot[data-content-slot-key="${CSS.escape(contentSlotKey)}"]`)) {
          return;
        }
        const contentSlotElem = this._generateContentSlotElement(contentSlot);
        if (!contentSlotElem) {
          return;
        }
        // Only once the card at its position is loaded (the grid can start at page N)
        const refNode = this.productGrid.querySelector(`.card--product[data-index="${contentSlot.position}"]`);
        if (!refNode) {
          return;
        }
        this.productGrid.insertBefore(contentSlotElem, refNode);
      });
    }

    // "Show previous" goes to the page before the first loaded one, and goes away on page 1
    if (this.showPreviousLink) {
      if (this.firstLoadedPage <= 1) {
        this.showPreviousLink.closest('.pagination')?.remove();
        this.showPreviousLink = null;
      } else {
        this.showPreviousLink.href = this.pageUrl(this.firstLoadedPage - 1);
      }
    }

    if (this.showCount) {
      // Cards in the grid, from the first loaded page to the last
      const showCount = this.productGrid.querySelectorAll('.card--product:not(.card--content-slot)').length;
      const countText = window.nostoStrings.searchShowCount || 'Showing {{ count }} of {{ total }} items';
      this.showCount.textContent = countText
        .replace('{{ count }}', showCount)
        .replace('{{ total }}', resultsTotal);
    }

    // Add event listeners for image loading
    this.setupImageLoadingEvents();

    // Deferred swatches, image carousels and touch CTA placement
    this.cardRenderer.hydrate(this.productGrid);

    // Replace nr of results in document title
    this.removeLoadingState();

    this.initialLoadComplete = true;

    // Dispatch custom event after Nosto products are rendered
    document.dispatchEvent(new CustomEvent('nosto:rendered', { detail: { placement: this } }));
  }

  processExcludeFacets(product) {
    const customFields = product.customFields;
    if (this.includeFacetRules && this.context === 'category') {
      let division;
      if (Array.isArray(customFields)) {
        division = customFields.find(f => f.key === 'd365-division')?.value?.toLowerCase();
      }
      if (division && !this.includeFacetRules[division]) {
        // Push all facets to excludeFacets where the division key doesn't match
        Object.keys(this.includeFacetRules).forEach(key => {
          if (key !== division) {
            this.excludeFacets.push(this.includeFacetRules[key]);
          }
        });
      }
    }
  }

  // New method to handle image loading events
  setupImageLoadingEvents() {
    const images = this.productGrid.querySelectorAll('img:not(.loaded)');
    images.forEach(img => {
      if (img.complete) {
        img.classList.add('loaded');
      } else {
        img.addEventListener('load', () => {
          img.classList.add('loaded');
        });
      }
    });
  }

  /**
   * Display an error state when both Nosto and fallback fail
   */
  renderErrorState() {
    if (!this.productGrid) return;

    this.productGrid.innerHTML = `
          <div class="mt-gutter mb-gutter">
            <p role="status">There was an error retrieving search results. Please try again later.</p>
            <a class="link" href="/">${window.nostoStrings?.continueShopping || 'Continue shopping'}</a>
          </div>
        `;
  }

  async renderFacets(facets) {
    if (!this.facetsFormContainer || !facets || !Array.isArray(facets)) {
      console.warn('Cannot render facets: Missing required elements or data');
      return;
    }

    // Store open facets state before clearing
    this.saveOpenFacetsState();

    // Get template references
    const templates = this.getFacetTemplates();
    if (!templates.facetsDetails) {
      console.error('Required facet templates not found');
      return;
    }

    // Clear existing facets
    this.facetsFormContainer.innerHTML = '';

    // Process each facet
    facets.forEach(facet => this.renderSingleFacet(facet, templates));

    // Add event listeners to new facet elements
    this.attachFacetEventListeners();

    // Scroll to last edited facet if applicable
    this.scrollToLastEditedFacet();
  }

  // Helper functions for facet rendering
  saveOpenFacetsState() {
    const openDetails = this.facetsFormContainer?.querySelectorAll('details[open]') || [];
    this.openedFacets = Array.from(openDetails).map(detail =>
      detail.getAttribute('data-filter-group')
    ).filter(Boolean);
  }

  getFacetTemplates() {
    return {
      facetsDetails: document.querySelector('#nosto-facets-details-template'),
      facetsListItem: document.querySelector('#nosto-facets-list-item-template'),
      facetsColor: document.querySelector('#nosto-facets-color-swatches-template'),
      facetsRange: document.querySelector('#nosto-facets-range-template')
    };
  }

  renderSingleFacet(facet, templates) {
    if (!facet || !facet.name) return;
    if (this.excludeFacets.includes(facet.name.toLowerCase())) return;

    const content = templates.facetsDetails.content.cloneNode(true);
    const tempContainer = document.createElement('div');
    tempContainer.appendChild(content);

    const tempContainerContentElem = tempContainer.querySelector('[data-facets-container]');
    const filterLabel = tempContainer.querySelector('summary span');
    filterLabel.textContent = facet.name.charAt(0).toUpperCase() + facet.name.slice(1).toLowerCase();

    // Set up details element
    const detailsElem = tempContainer.querySelector('details');
    detailsElem.setAttribute('data-filter-group', facet.name);

    // Restore open state if this facet was open before
    if (this.openedFacets.includes(facet.name)) {
      detailsElem.setAttribute('open', '');
    }

    let facetActive = false;

    // Handle different facet types
    if (facet.type === 'terms') {
      facetActive = this.renderTermsFacet(facet, tempContainerContentElem, templates);
    } else if (facet.type === 'stats' && facet.name === "Price") {
      facetActive = this.renderPriceFacet(facet, tempContainerContentElem, templates);
    }

    // Toggle clear button visibility
    const clearButton = tempContainer.querySelector('.facet-clear');
    if (clearButton) {
      clearButton.style.display = facetActive ? 'block' : 'none';
    }

    // Add to the DOM
    this.facetsFormContainer.appendChild(tempContainer.firstElementChild);
  }

  renderTermsFacet(facet, containerElem, templates) {
    let facetActive = false;

    // Determine filter type
    let filterType = 'default';
    if (facet.name === 'Colour') {
      filterType = 'color';
    }

    // Create list container
    const listContainer = document.createElement('ul');
    listContainer.classList.add('filter-options-list', filterType);
    containerElem.appendChild(listContainer);

    // Add each facet value
    facet.data.forEach(item => {
      const content = templates.facetsListItem.content.cloneNode(true);
      const listItemContainer = document.createElement('li');
      listItemContainer.classList.add('filter-option');
      listItemContainer.appendChild(content);

      // Get parameter name, using mapping if available
      const facetNameLower = facet.name.toLowerCase().replace(' ', '-');
      const paramName = window.nostoToFilter && window.nostoToFilter[facet.field]
        ? window.nostoToFilter[facet.field]
        : facetNameLower;

      if (!window.nostoToFilter[facet.field]) {
        window.filterToNosto[facetNameLower] = facet.field;
      }

      // Fill the template through the DOM: facet values come from Nosto and
      // must not be parsed as HTML
      listItemContainer.querySelector('label').classList.add(`filter-type-${filterType}`);
      const input = listItemContainer.querySelector('input');
      input.setAttribute('data-param-name', paramName);
      input.setAttribute('data-value', item.value);
      input.setAttribute('data-key-value', `${paramName}:${item.value}`);
      listItemContainer.querySelector('[data-option-label]').textContent = item.value;
      listItemContainer.querySelector('[data-option-count]').textContent = `(${item.count})`;

      const inputClassType = filterType === 'color' ? 'for-swatch' : 'checkbox';
      input.classList.add(inputClassType);

      // Handle color swatches
      if (filterType === 'color') {
        this.addColorSwatch(listItemContainer, item.value, templates);
      }

      // Set checked state
      if (item.selected) {
        input.checked = true;
        facetActive = true;
      } else {
        input.removeAttribute('checked');
      }

      listContainer.appendChild(listItemContainer);
    });

    // Add search input for facets that should be searchable
    if (this.searchableFacets.includes(facet.field.toLowerCase())) {
      this.renderFacetSearch(listContainer, facet.field);
    }

    return facetActive;
  }

  addColorSwatch(container, colorValue, templates) {
    const content = templates.facetsColor.content.cloneNode(true);
    const swatchContainer = document.createElement('span');
    swatchContainer.classList.add('swatch');
    swatchContainer.appendChild(content);

    // Get color value, using mapping if available
    let facetColor = colorValue.toLowerCase();
    if (window.colorPatterns && window.colorPatterns[facetColor]) {
      facetColor = window.colorPatterns[facetColor];
    }

    swatchContainer.querySelector('.color').style.setProperty('--color', facetColor);
    swatchContainer.querySelector('.visually-hidden').textContent = colorValue;

    const spanLabel = container.querySelector('span[data-option-label]');
    container.querySelector('label').insertBefore(swatchContainer, spanLabel);
  }

  renderPriceFacet(facet, containerElem, templates) {
    const content = templates.facetsRange.content.cloneNode(true);
    const priceRangeTempContainer = document.createElement('div');
    priceRangeTempContainer.appendChild(content);

    const params = new URLSearchParams(window.location.search);
    const priceParam = params.get('filter.price');
    let newMinValue = 0;
    let newMaxValue = facet.max;
    let maxPrice = facet.max;
    let facetActive = false;

    if (priceParam) {
      const [range, max] = priceParam.split('~');
      const [gte, lte] = range.split('-');
      newMinValue = parseInt(gte) || 0;
      newMaxValue = parseInt(lte) || facet.max;
      maxPrice = parseInt(max) || facet.max;
      facetActive = true;
    } else {
      this.activeMaxPrice = facet.max;
    }

    // <price-range> reads its data attributes in its constructor, which runs
    // when the facet is added to the page, so set them before that
    const priceRange = priceRangeTempContainer.querySelector('price-range');
    priceRange.setAttribute('data-min-value', newMinValue);
    priceRange.setAttribute('data-max-value', newMaxValue);
    priceRange.setAttribute('data-max', maxPrice);
    const inputMin = priceRangeTempContainer.querySelector('.price-range__input-min');
    inputMin.setAttribute('max', maxPrice - 1);
    inputMin.setAttribute('value', newMinValue);
    const inputMax = priceRangeTempContainer.querySelector('.price-range__input-max');
    inputMax.setAttribute('min', newMinValue + 1);
    inputMax.setAttribute('max', maxPrice);
    inputMax.setAttribute('value', newMaxValue);

    containerElem.appendChild(priceRangeTempContainer);

    return facetActive;
  }

  attachFacetEventListeners() {
    // Add change event listeners to inputs
    this.facetsFormContainer.querySelectorAll('input').forEach((item) => {
      item.addEventListener('change', debounce(this.boundFacetChangeHandler, 150));
    });

    // Add click event listeners to clear buttons
    this.facetsFormContainer.querySelectorAll('.facet-clear').forEach((item) => {
      item.addEventListener('click', debounce(this.boundFacetClearHandler, 150));
    });

    // Initialize accordions for details elements
    this.facetsFormContainer.querySelectorAll('details').forEach((el) => {
      new Accordion(el);
    });
  }

  scrollToLastEditedFacet() {
    if (!this.lastEditedFacet) return;

    const lastEditedFacetInput = this.facetsFormContainer.querySelector(
      `.filter-option input[data-key-value="${CSS.escape(this.lastEditedFacet)}"]`
    );

    if (lastEditedFacetInput) {
      // Use smooth scrolling and add slight delay to ensure the facet is open
      setTimeout(() => {
        lastEditedFacetInput.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }

  renderFacetSearch(container, facetTitle) {
    const searchLabel = document.createElement('label');
    const inputTextContent = `${window.nostoStrings.search} ${facetTitle}s`;
    searchLabel.textContent = inputTextContent;
    searchLabel.classList.add('visually-hidden'); // Hides label visually but keeps it accessible

    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.placeholder = inputTextContent;
    searchInput.classList.add('facet-search-field');
    searchInput.setAttribute('aria-label', inputTextContent);

    const searchWrapper = document.createElement('div');
    searchWrapper.classList.add('facet-search');
    searchWrapper.appendChild(searchLabel);
    searchWrapper.appendChild(searchInput);

    container.parentNode.insertBefore(searchWrapper, container);

    searchInput.addEventListener('input', (e) => {
      const searchValue = e.target.value.toLowerCase();
      const listItems = container.querySelectorAll('li');
      listItems.forEach(item => {
        const label = item.querySelector('[data-option-label]').textContent.toLowerCase();
        item.style.display = label.includes(searchValue) ? '' : 'none';
      });
    });
  }

  async onFacetChange(event) {
    if (this.usingFallback) {
      // Let Shopify facets handle it
      return;
    }
    const target = event.target;
    if (target.classList.contains('facet-search-field')) return;
    const params = new URLSearchParams(window.location.search);
    const isPriceRange = target.classList.contains('price-range__input');
    const paramTitle = target.getAttribute('data-param-name').toLowerCase();

    // Reset pagination when changing filters
    params.delete('page');
    this.firstLoadedPage = 1;
    this.lastLoadedPage = 1;

    if (isPriceRange) {
      // Every price facet change triggers events for both min and max inputs, skip the min input and handle only the event from max input
      // If selected value is out of bounds, remove filter value from parameters
      const inputMin = target.getAttribute('name') === 'filter.price.gte';
      const inputMax = target.getAttribute('name') === 'filter.price.lte';
      if (inputMin && target.value < Math.floor(target.min)) {
        target.value = target.min;
        return;
      } else if (inputMax && target.value > Math.floor(target.max)) {
        target.value = target.max;
        return;
      } else {
        const minInput = this.facetsFormContainer.querySelector('input[name="filter.price.gte"]');
        const maxInput = this.facetsFormContainer.querySelector('input[name="filter.price.lte"]');

        // Skip if values are out of bounds
        if (parseFloat(minInput.value) < parseFloat(minInput.min) ||
          parseFloat(maxInput.value) > parseFloat(maxInput.max)) {
          return;
        }

        // Construct the new price parameter
        const priceValue = `${minInput.value}-${maxInput.value}`;
        if (this.activeMaxPrice) {
          params.set('filter.price', `${priceValue}~${this.activeMaxPrice}`);
        } else {
          params.set('filter.price', priceValue);
        }
      }
    }

    const keyValue = target.getAttribute('data-key-value');
    const param = `filter.${paramTitle}`;
    const value = target.getAttribute('data-value') || target.value;

    if (!param || !value) return;

    const existingValues = params.get(param)?.split('|') || [];
    if (!isPriceRange) {
      if (target.checked) {
        existingValues.push(value);
        params.set(param, existingValues.join('|'));
        this.lastEditedFacet = keyValue;
      } else {
        const newValues = existingValues.filter(v => v !== value);
        if (newValues.length === 0) {
          params.delete(param);
          this.lastEditedFacet = null;
        } else {
          params.set(param, newValues.join('|'));
        }
      }
    }

    this.updateQuery(params);
    // Skip event from min price input, price change is handled from max input
    if (target.getAttribute('data-min') !== null) return;
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  async onFacetClear(event) {
    event.preventDefault();
    const target = event.target;
    const params = new URLSearchParams(window.location.search);
    const parent = target.closest('details');
    const field = parent.getAttribute('data-filter-group')?.toLowerCase();

    // Reset pagination when clearing filters
    params.delete('page');
    this.firstLoadedPage = 1;
    this.lastLoadedPage = 1;

    if (field == 'price') {
      params.delete('filter.price');
      this.activeMaxPrice = null;
    } else {
      const inputs = parent.querySelectorAll('input');
      inputs.forEach((input) => {
        input.checked = false;
      });

      params.delete(`filter.${field}`);
      this.lastEditedFacet = null;
    }

    this.updateQuery(params);
    this.setLoadingState();
    this.getNostoSearchResults();
    this.getSearchResults();
  }

  /**
   * Nosto has no results for this page: the same message as the server's
   * out-of-range page, kept out of the index
   */
  renderOutOfRange() {
    const outOfRangeText = window.nostoStrings?.outOfRange || "This page doesn't exist.";
    const firstPageText = window.nostoStrings?.firstPage || 'Go to the first page';

    this.productGrid.innerHTML = `
          <div class="mt-gutter mb-gutter text-container" data-out-of-range>
            <p role="status">${outOfRangeText}</p>
            <a class="link" href="${this.pageUrl(1)}">${firstPageText}</a>
          </div>
        `;
    this.showPreviousLink?.closest('.pagination')?.remove();
    this.showPreviousLink = null;
    if (this.viewMoreButton) {
      this.viewMoreButton.hidden = true;
      this.viewMoreButton.removeAttribute('href');
    }
    if (!document.querySelector('meta[name="robots"]')) {
      const robots = document.createElement('meta');
      robots.name = 'robots';
      robots.content = 'noindex';
      document.head.appendChild(robots);
    }
    this.removeLoadingState();
  }

  renderNoResults() {
    if (!this.productGrid) return;

    const noResultsMessage = window.nostoStrings?.searchNoResults || 'No results for "{{terms}}"';
    const continueShoppingText = window.nostoStrings?.continueShopping || 'Continue shopping';
    this.classList.add('no-results');

    this.productGrid.innerHTML = `
          <div class="mt-gutter mb-gutter text-container">
            <p role="status">${noResultsMessage.replace('{{ terms }}', this.searchTerm || '')}</p>
            <a class="link" href="/">${continueShoppingText}</a>
          </div>
        `;
    if (this.viewMoreButton) {
      this.viewMoreButton.hidden = true;
      this.viewMoreButton.removeAttribute('href');
    }
    this.removeLoadingState();

    // Update page title
    if (this.context === 'serp') {
      document.title = `Search: No results for "${this.searchTerm}"`;
    }
  }
}

if (!customElements.get('nosto-search')) {
  customElements.define('nosto-search', NostoSearch);
}
